import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Eye, Play, Pause, Trash2, Calendar, Users, Send, Mail, MessageSquare, Copy } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const CampaignList = () => {
  const queryClient = useQueryClient();

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ["campaigns"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaigns")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("campaigns")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success("Campaign deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete campaign");
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      // If status is "sending", call the send-campaign edge function
      if (status === "sending") {
        const { data, error } = await supabase.functions.invoke("send-campaign", {
          body: { campaignId: id },
        });
        
        if (error) throw error;
        
        console.log("Campaign send response:", data);
        return data;
      } else {
        // Just update the status
        const { error } = await supabase
          .from("campaigns")
          .update({ status })
          .eq("id", id);
        if (error) throw error;
      }
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      if (variables.status === "sending") {
        toast.success(`Campaign sent! ${data?.emailsSent || 0} emails, ${data?.smsSent || 0} SMS`);
      } else {
        toast.success("Campaign status updated");
      }
    },
    onError: (error: any) => {
      toast.error(`Failed: ${error.message}`);
    },
  });

  const duplicateAndSendMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      // Get the original campaign
      const { data: originalCampaign, error: fetchError } = await supabase
        .from("campaigns")
        .select("*")
        .eq("id", campaignId)
        .single();

      if (fetchError || !originalCampaign) throw new Error("Failed to fetch campaign");

      // Create a duplicate campaign with same data
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data: newCampaign, error: createError } = await supabase
        .from("campaigns")
        .insert([{
          user_id: user.id,
          name: `${originalCampaign.name} (Copy)`,
          description: originalCampaign.description,
          type: originalCampaign.type,
          subject: originalCampaign.subject,
          message: originalCampaign.message,
          segment_type: originalCampaign.segment_type,
          segment_filters: originalCampaign.segment_filters,
          recipient_count: originalCampaign.recipient_count,
          status: "sending",
          scheduled_at: null, // Send immediately
        }])
        .select()
        .single();

      if (createError || !newCampaign) throw new Error("Failed to create duplicate campaign");

      // Send the new campaign immediately
      const { data: sendData, error: sendError } = await supabase.functions.invoke("send-campaign", {
        body: { campaignId: newCampaign.id },
      });

      if (sendError) throw sendError;

      return { newCampaign, sendResult: sendData };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success(
        `Campaign duplicated and sent! ${data.sendResult?.emailsSent || 0} emails, ${data.sendResult?.smsSent || 0} SMS`
      );
    },
    onError: (error: any) => {
      console.error("Duplicate and send error:", error);
      toast.error(`Failed to duplicate and send: ${error.message}`);
    },
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      draft: "secondary",
      scheduled: "default",
      sending: "default",
      sent: "default",
      paused: "secondary",
      failed: "destructive",
    };
    return <Badge variant={variants[status] || "secondary"}>{status}</Badge>;
  };

  if (isLoading) {
    return <div>Loading campaigns...</div>;
  }

  if (!campaigns || campaigns.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">
            No campaigns yet. Create your first campaign to get started!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {campaigns.map((campaign) => (
        <Card key={campaign.id}>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2">
                  {campaign.type === "email" ? (
                    <Mail className="h-4 w-4" />
                  ) : campaign.type === "sms" ? (
                    <MessageSquare className="h-4 w-4" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {campaign.name}
                </CardTitle>
                <CardDescription>{campaign.description}</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {getStatusBadge(campaign.status)}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>{campaign.recipient_count} recipients</span>
                </div>
                <div className="flex items-center gap-2">
                  <Send className="h-4 w-4 text-muted-foreground" />
                  <span>{campaign.sent_count} sent</span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-muted-foreground" />
                  <span>{campaign.opened_count} opened</span>
                </div>
                {campaign.scheduled_at && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{format(new Date(campaign.scheduled_at), "MMM d, p")}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2 flex-wrap">
                {campaign.status === "draft" && (
                  <Button
                    size="sm"
                    onClick={() => updateStatusMutation.mutate({ id: campaign.id, status: "sending" })}
                    disabled={updateStatusMutation.isPending}
                  >
                    <Play className="mr-2 h-4 w-4" />
                    Send Now
                  </Button>
                )}
                {campaign.status === "sending" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatusMutation.mutate({ id: campaign.id, status: "paused" })}
                    disabled={updateStatusMutation.isPending}
                  >
                    <Pause className="mr-2 h-4 w-4" />
                    Pause
                  </Button>
                )}
                {campaign.status === "paused" && (
                  <Button
                    size="sm"
                    onClick={() => updateStatusMutation.mutate({ id: campaign.id, status: "sending" })}
                    disabled={updateStatusMutation.isPending}
                  >
                    <Play className="mr-2 h-4 w-4" />
                    Resume
                  </Button>
                )}
                {campaign.status === "sent" && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="default" disabled={duplicateAndSendMutation.isPending}>
                        <Copy className="mr-2 h-4 w-4" />
                        Duplicate & Send Again
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Duplicate & Send Campaign</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will create a copy of this campaign with the same content and recipients, and send it immediately.
                          The campaign will be sent to <strong>{campaign.recipient_count} recipients</strong>.
                          <br /><br />
                          Are you sure you want to proceed?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => duplicateAndSendMutation.mutate(campaign.id)}>
                          Yes, Send Now
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="destructive">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Campaign</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this campaign? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => deleteMutation.mutate(campaign.id)}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
